import React, { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, LoaderSizes, Button } from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "./redux";
import HIDCustomLogo from "./pages/auth/login/HIDCustomLogo";
import {
  usePartnerSessionAllModulesDenied,
  usePartnerSessionCanAccessAnyModule,
  usePartnerSessionCanAccessModule,
  usePartnerSessionCanEditModule,
  usePartnerSessionModuleDenied,
  usePartnerSessionPermissionsLoaded,
  usePartnerSessionPermissionsLoading,
} from "./hooks/usePartnerSessionPermissions";

/** Route guard using sessionPartnerPermissionMap (partner portal matrix). */
const PartnerSessionProtectedRoute: React.FC<{
  element: ReactElement;
  requiredPermissionKey?: string;
  /** When set, OR-check these keys instead of only requiredPermissionKey. */
  requiredPermissionKeys?: readonly string[];
  requireEdit?: boolean;
}> = ({
  element,
  requiredPermissionKey = "",
  requiredPermissionKeys,
  requireEdit = false,
}) => {
  const navigate = useNavigate();
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loading = usePartnerSessionPermissionsLoading();
  const loaded = usePartnerSessionPermissionsLoaded();
  const keys =
    requiredPermissionKeys ??
    (requiredPermissionKey ? [requiredPermissionKey] : []);
  const useAny = keys.length > 1;
  const canAccessSingle = usePartnerSessionCanAccessModule(keys[0] ?? "");
  const canEditSingle = usePartnerSessionCanEditModule(keys[0] ?? "");
  const deniedSingle = usePartnerSessionModuleDenied(keys[0] ?? "");
  const canAccessAny = usePartnerSessionCanAccessAnyModule(keys);
  const deniedAll = usePartnerSessionAllModulesDenied(keys);
  const canAccess = useAny ? canAccessAny : canAccessSingle;
  const denied = useAny ? deniedAll : deniedSingle;
  const allowed = requireEdit ? canEditSingle : canAccess;

  if (roleId && loading) {
    return (
      <div className="signing-in-container">
        <Loader variant={LoaderSizes.large} />
      </div>
    );
  }

  if (roleId && loaded && (denied || !allowed)) {
    return (
      <div className="signing-in-container">
        <div className="logo-wrapper">
          <HIDCustomLogo />
        </div>
        <div className="message-box">
          <p>Permission Denied</p>
          <Button variant="primary" label="Back" onClick={() => navigate(-1)} />
        </div>
        </div>
    );
  }

  return element;
};

export default PartnerSessionProtectedRoute;
